/**
 * The filter bar both game pages share: what is filtering right now, each one
 * removable, plus one clear-everything button. The state lives in the page URL,
 * so a ten-second live refresh cannot reset it and a filtered view can be sent
 * to somebody else.
 */
import { useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { GamePill, TONES } from './GamePlayerChips';
import { statusKey, resultKey, pickKey, prizeKey, voucherKey, giftCardKey } from './GameDonuts';

export const DONUT_IDS = ['status', 'result', 'pick', 'prize', 'voucher', 'giftcard'];

const KEY_OF = {
    status: statusKey, result: resultKey, pick: pickKey,
    prize: prizeKey, voucher: voucherKey, giftcard: giftCardKey,
};

/** Every chosen slice must match — combined filters narrow, they never widen. */
export const rowMatches = (row, active) => DONUT_IDS.every((id) => {
    const chosen = active[id] || [];
    return !chosen.length || chosen.includes(KEY_OF[id](row));
});

/** The words a chosen slice shows in the bar. A prize is its own name. */
export const filterLabel = (t, id, key) => {
    if (id === 'prize') return key;
    if (id === 'giftcard') return t(`gameBoard.giftCard.${key}`, key);
    const group = id === 'voucher' ? 'voucher' : id;
    return t(`gameBoard.${group}.${key}`, key);
};

/** Read and write the active filters in the page URL. */
export const useGameFilters = (params, setParams) => {
    const active = useMemo(() => {
        const out = {};
        DONUT_IDS.forEach((id) => {
            const raw = params.get(`f_${id}`);
            out[id] = raw ? raw.split('~').filter(Boolean) : [];
        });
        return out;
    }, [params]);

    const write = useCallback((next) => {
        const copy = new URLSearchParams(params);
        DONUT_IDS.forEach((id) => {
            const list = next[id] || [];
            if (list.length) copy.set(`f_${id}`, list.join('~'));
            else copy.delete(`f_${id}`);
        });
        setParams(copy, { replace: true });
    }, [params, setParams]);

    const toggle = useCallback((id, key) => {
        const current = active[id] || [];
        write({ ...active,
            [id]: current.includes(key) ? current.filter((k) => k !== key) : [...current, key] });
    }, [active, write]);

    const clear = useCallback(() => write({}), [write]);

    const count = DONUT_IDS.reduce((n, id) => n + (active[id] || []).length, 0);
    return { active, toggle, clear, count };
};

export const GameFilterBar = ({ active, labelFor, onToggle, onClear, count, t, prefix, children }) => (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-filter-bar`}>
        {children}
        {count > 0 && (
            <>
                <span className="text-xs text-muted-foreground" data-testid={`${prefix}-filters-label`}>
                    {t('gameBoard.filtering', 'Showing only')}
                </span>
                {DONUT_IDS.flatMap((id) => (active[id] || []).map((key) => (
                    <GamePill key={`${id}:${key}`} tone={TONES.blue}
                              label={`${labelFor(id, key)} ✕`}
                              onClick={() => onToggle(id, key)}
                              testId={`${prefix}-active-filter-${id}-${key}`} />
                )))}
                <Button variant="outline" size="sm" onClick={onClear}
                        className="h-8" data-testid={`${prefix}-clear-filters`}>
                    <X className="w-3.5 h-3.5 mr-1" />
                    {t('gameBoard.clearFilters', 'Clear filters')}
                </Button>
            </>
        )}
    </div>
);
